import { format as formatDateFns, isSameDay, isSameYear, Locale, subDays } from "date-fns";
import { bg } from "date-fns/locale";

const toValidDate = (dateInput?: Date | string | null): Date | null => {
  if (!dateInput) return null;
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  return isNaN(date.getTime()) ? null : date;
};

// Short day label relative to `now`: "Вчера", "12 окт" or "12/10/2025"; null for today.
const relativeDayLabel = (date: Date, now: Date): string | null => {
  if (isSameDay(date, now)) return null;
  if (isSameDay(date, subDays(now, 1))) return "Вчера";
  if (isSameYear(date, now)) return formatDateFns(date, "d MMM", { locale: bg });
  return formatDateFns(date, "dd/MM/yyyy");
};

export const dateUtility = {
  formatDate: (
    dateInput?: Date | string | null,
    formatStr?: string,
    locale?: Locale
  ) => {
    if (!dateInput) return "N/A";
    let date: Date | null = null;
    if (typeof dateInput === "string") {
      const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dateInput);
      if (match) {
        const [, day, month, year] = match;
        date = new Date(`${year}-${month}-${day}`);
      } else {
        date = new Date(dateInput);
      }
    } else {
      date = dateInput;
    }
    if (!date || isNaN(date.getTime())) return "N/A";
    if (formatStr) {
      try {
        return formatDateFns(date, formatStr, locale ? { locale } : undefined);
      } catch {
        return "N/A";
      }
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  },

  formatDateTime: (dateInput?: Date | string | null) => {
    if (!dateInput) {
      return "N/A";
    }

    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
      return "N/A";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hour = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    const formattedDate = `${day}/${month}/${year}, ${hour}:${minutes}`;

    return formattedDate;
  },

  /** Compact date for lists: "16:40" today, then "Вчера", "12 окт", "12/10/2025". */
  formatRelativeDate: (dateInput?: Date | string | null, now: Date = new Date()) => {
    const date = toValidDate(dateInput);
    if (!date) return "N/A";
    return relativeDayLabel(date, now) ?? formatDateFns(date, "HH:mm");
  },

  /** Date with time for messages: "16:40" today, then "Вчера, 16:40", "12 окт, 16:40", "12/10/2025, 16:40". */
  formatRelativeDateTime: (dateInput?: Date | string | null, now: Date = new Date()) => {
    const date = toValidDate(dateInput);
    if (!date) return "N/A";
    const time = formatDateFns(date, "HH:mm");
    const day = relativeDayLabel(date, now);
    return day ? `${day}, ${time}` : time;
  },

  formatDateForHref: (dateInput?: Date | null, reverse: boolean = false) => {
    if (!dateInput) {
      return;
    }
    const day = String(dateInput.getDate()).padStart(2, "0");
    const month = String(dateInput.getMonth() + 1).padStart(2, "0");
    const year = dateInput.getFullYear();

    if (reverse) {
      return `${year}-${month}-${day}`;
    }
    return `${day}-${month}-${year}`;
  },

  resetTime: (date?: Date | null) => {
    if (!date) {
      return;
    }
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0, // Hours
        0, // Minutes
        0, // Seconds
        0 // Milliseconds
      )
    );
  },
  parseDateForPlanner: (dateString: string) => {
    if (!dateString) return new Date();
    const [year, month, day] = dateString.split("-").map(Number);

    return new Date(year, month - 1, day);
  },
};
